import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { BrandLockup } from "@/shell/CandidateFlow";

// Standalone, chrome-free render of BrandLockup for Brand Setup's preview
// iframes. Exists because CSS media queries key off the real browser
// viewport, not a parent container's width — embedding BrandLockup directly
// in a narrow <div> on the Brand Setup page can't actually trigger its
// mobile breakpoint. An iframe gets its own independent viewport, so sizing
// the iframe element itself produces a real mobile/desktop render, matching
// exactly what a candidate's phone or laptop would show (HomeShell/LiveSession
// use this same component the same way). No auth: renders only the public
// branding assets/agency name already visible on the live candidate pages.
export function BrandPreview() {
  const [params] = useSearchParams();
  const agencyName = params.get("agencyName") || undefined;
  // logoUrl travels via sessionStorage, not the URL — see DevicePreview in
  // BrandSetup.tsx for why (data: URI logos can exceed the request-line limit).
  const logoKey = params.get("logoKey");
  const logoUrl = (logoKey ? sessionStorage.getItem(logoKey) : undefined) || undefined;
  const logoFilter = params.get("logoFilter") || undefined;
  // CSS custom properties do not cross an iframe boundary, so the parent
  // page's --logo-backdrop is invisible in here. Without this the preview
  // renders the logo on nothing while the cards next to it show it on the
  // agency's colour, which is exactly the disagreement a preview exists to
  // prevent. Short hex, so the URL is safe (the logo itself still travels
  // via sessionStorage).
  const logoBackdrop = params.get("logoBackdrop") || undefined;
  const dark = params.get("dark") === "1";
  const inline = params.get("inline") === "1";
  const align = params.get("align") === "left" ? "left" : "center";

  // The staging banner (injected server-side, before <div id="root">) reserves
  // real layout space via its own spacer div, pushing this wrapper down from
  // the top of the document. "100vh" ignores that offset, so a fixed-height
  // iframe (see DevicePreview in BrandSetup.tsx) ends up shorter than the
  // wrapper's true rendered height — content overflows the iframe's own
  // viewport, triggering a native scrollbar, and centers itself relative to a
  // box taller than what's actually visible, landing off-centre. Measuring
  // the wrapper's own offset (whatever pushed it down, not a hardcoded banner
  // height) and subtracting it fixes both at once.
  const wrapRef = useRef<HTMLDivElement>(null);
  const [minHeight, setMinHeight] = useState("100vh");
  useEffect(() => {
    if (wrapRef.current) {
      setMinHeight(`calc(100vh - ${wrapRef.current.getBoundingClientRect().top}px)`);
    }
  }, []);

  return (
    <div
      ref={wrapRef}
      style={{
        minHeight,
        background: dark ? "#17131F" : "#FBFAFE",
        display: "flex",
        alignItems: "center",
        justifyContent: inline || align === "left" ? "flex-start" : "center",
        padding: 16,
        boxSizing: "border-box",
      }}
    >
      <div style={logoBackdrop ? ({ ["--logo-backdrop" as string]: logoBackdrop } as React.CSSProperties) : undefined}>
        <BrandLockup agencyName={agencyName} logoUrl={logoUrl} logoFilter={logoFilter} dark={dark} inline={inline} align={align} />
      </div>
    </div>
  );
}
